import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import CheckboxCell from './cells/CheckboxCell';
import NameCell from './cells/NameCell';
import StatusCell from './cells/StatusCell';
import PriorityCell from './cells/PriorityCell';
import DifficultyCell from './cells/DifficultyCell';
import DependencyCell from './cells/DependencyCell';
import TagsCell from './cells/TagsCell';
import ActionsCell from './cells/ActionsCell';
import InputsCell from './cells/InputsCell';
import DeliverablesCell from './cells/DeliverablesCell';
import AttachmentsCell from './cells/AttachmentsCell';
import CustomFieldCell from './cells/CustomFieldCell';
import { 
  DueDateCell, 
  EstimatedDurationCell, 
  CreatedAtCell, 
  StartedAtCell, 
  CompletedAtCell, 
  UpdatedAtCell 
} from './cells/DateCell';
import HeaderCheckbox from './headers/HeaderCheckbox';

const columnHelper = createColumnHelper();

/**
 * Base column definitions - created once and reused
 * This prevents creating new column objects on every render
 */
const BASE_COLUMNS = [
    // ═══════════════════════════════════════════════════════════════
    // MANDATORY COLUMNS (always visible)
    // ═══════════════════════════════════════════════════════════════
    
    // Selection checkbox column
    columnHelper.display({
      id: 'select',
      size: 48,
      minSize: 48,
      maxSize: 48,
      enableResizing: false,
      header: ({ table }) => <HeaderCheckbox table={table} />,
      cell: ({ row }) => <CheckboxCell row={row} />,
      meta: {
        sticky: true,
        stickyLeft: 0,
        mandatory: true,
      },
    }),

    // Task name column
    columnHelper.accessor('name', {
      id: 'name',
      size: 250,
      minSize: 150,
      header: 'Mission',
      cell: ({ row, getValue }) => (
        <NameCell row={row} value={getValue()} />
      ),
      meta: {
        sticky: true,
        mandatory: true,
      },
    }),

    // Status column
    columnHelper.accessor('status', {
      id: 'status',
      size: 120,
      minSize: 100,
      header: 'Status',
      cell: ({ row }) => <StatusCell row={row} />,
      meta: {
        mandatory: true,
      },
    }),

    // ═══════════════════════════════════════════════════════════════
    // OPTIONAL COLUMNS (can be toggled)
    // ═══════════════════════════════════════════════════════════════

    // Priority column
    columnHelper.accessor('priority', {
      id: 'priority',
      size: 100,
      minSize: 80,
      header: 'Priority',
      cell: ({ row }) => <PriorityCell row={row} />,
    }),

    // Difficulty column
    columnHelper.accessor('difficulty', {
      id: 'difficulty',
      size: 110,
      minSize: 90,
      header: 'Difficulty',
      cell: ({ row }) => <DifficultyCell row={row} />,
    }),

    // Conditions column (unlock conditions / dependencies)
    columnHelper.accessor('dependsOn', {
      id: 'conditions',
      size: 200,
      minSize: 120,
      header: 'Conditions',
      cell: ({ row }) => <DependencyCell row={row} />,
    }),

    // Tags column (multi-select, uses shared TagsDropdown from Flow/Kanban)
    columnHelper.accessor('tags', {
      id: 'tags',
      size: 180,
      minSize: 120,
      header: 'Tags',
      cell: ({ row }) => <TagsCell row={row} />,
    }),

    // Inputs column
    columnHelper.accessor('inputs', {
      id: 'inputs',
      size: 150,
      minSize: 80,
      header: 'Inputs',
      cell: ({ row }) => <InputsCell row={row} />,
    }),

    // Deliverables column
    columnHelper.accessor('deliverables', {
      id: 'deliverables',
      size: 120,
      minSize: 80,
      header: 'Deliverables',
      cell: ({ row }) => <DeliverablesCell row={row} />,
    }),

    // Attachments column
    columnHelper.accessor('attachments', {
      id: 'attachments',
      size: 100,
      minSize: 80,
      header: 'Files',
      cell: ({ row }) => <AttachmentsCell row={row} />,
    }),

    // Due Date column
    columnHelper.accessor('dueDate', {
      id: 'dueDate',
      size: 110,
      minSize: 90,
      header: 'Due Date',
      cell: ({ row }) => <DueDateCell row={row} />,
    }),

    // Estimated duration column
    columnHelper.accessor('estimatedDurationMinutes', {
      id: 'estimatedDurationMinutes',
      size: 120,
      minSize: 90,
      header: 'Est. time',
      cell: ({ row }) => <EstimatedDurationCell row={row} />,
    }),

    // Created Date column
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      size: 110,
      minSize: 90,
      header: 'Created',
      cell: ({ row }) => <CreatedAtCell row={row} />,
    }),

    // Start Date column
    columnHelper.accessor('startedAt', {
      id: 'startedAt',
      size: 110,
      minSize: 90,
      header: 'Start Date',
      cell: ({ row }) => <StartedAtCell row={row} />,
    }),

    // Completed Date column
    columnHelper.accessor('completedAt', {
      id: 'completedAt',
      size: 110,
      minSize: 90,
      header: 'Completed',
      cell: ({ row }) => <CompletedAtCell row={row} />,
    }),

    // Last Updated column
    columnHelper.accessor('updatedAt', {
      id: 'updatedAt',
      size: 120,
      minSize: 90,
      header: 'Updated',
      cell: ({ row }) => <UpdatedAtCell row={row} />,
    }),

    // ═══════════════════════════════════════════════════════════════
    // ACTIONS COLUMN (mandatory, always last)
    // ═══════════════════════════════════════════════════════════════
    
    // Actions column
    columnHelper.display({
      id: 'actions',
      size: 185, // Increased to accommodate details button
      minSize: 185,
      enableResizing: false,
      header: 'Functions',
      cell: ({ row }) => <ActionsCell row={row} />,
      meta: {
        mandatory: true,
      },
    }),
];

/**
 * Create a cache key from column visibility and custom fields
 */
const getVisibilityKey = (columnVisibility, customFields = []) => {
  const visibilityPart = Object.entries(columnVisibility || {})
    .filter(([, visible]) => visible === false)
    .map(([id]) => id)
    .sort()
    .join(',') || 'default';
  
  // Include custom field IDs in cache key
  const customFieldsPart = customFields
    .map(f => f.id)
    .sort()
    .join(',') || 'none';
  
  return `${visibilityPart}|${customFieldsPart}`;
};

// Cache for filtered columns based on visibility key
const columnCache = new Map();

/**
 * Create dynamic columns for custom fields
 */
const createCustomFieldColumns = (customFields = [], columnVisibility = {}) => {
  return customFields
    .filter(field => columnVisibility[field.id] !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(field => 
      columnHelper.display({
        id: field.id,
        size: 120,
        minSize: 80,
        header: field.name,
        cell: ({ row }) => <CustomFieldCell row={row} fieldId={field.id} />,
        meta: {
          isCustomField: true,
        },
      })
    );
};

/**
 * Column definitions for the task table
 * Uses TanStack Table's columnHelper for type safety and better DX
 * 
 * Mandatory columns: select, name, status, actions
 * Optional columns: priority, difficulty, conditions, tags, inputs, deliverables, 
 *                   dueDate, estimatedDurationMinutes, createdAt, startedAt, completedAt, updatedAt
 * Custom field columns: dynamically generated from campaign custom fields
 * 
 * Performance: Columns are cached based on visibility settings and custom fields
 * to prevent creating new column arrays on every render.
 */
export const createColumns = (columnVisibility = {}, customFields = []) => {
  const cacheKey = getVisibilityKey(columnVisibility, customFields);
  
  // Return cached columns if available
  if (columnCache.has(cacheKey)) {
    return columnCache.get(cacheKey);
  }
  
  // Filter base columns based on visibility
  const filteredBaseColumns = BASE_COLUMNS.filter(col => {
    const colId = col.id;
    // Mandatory columns are always visible
    if (col.meta?.mandatory) return true;
    // Check visibility setting (default to true for columns if not set)
    return columnVisibility[colId] !== false;
  });
  
  // Create custom field columns
  const customFieldColumns = createCustomFieldColumns(customFields, columnVisibility);
  
  // Insert custom field columns before the actions column
  const actionsColumn = filteredBaseColumns.find(col => col.id === 'actions');
  const otherColumns = filteredBaseColumns.filter(col => col.id !== 'actions');
  
  const allColumns = [
    ...otherColumns,
    ...customFieldColumns,
    ...(actionsColumn ? [actionsColumn] : []),
  ];
  
  // Cache the result
  columnCache.set(cacheKey, allColumns);
  
  return allColumns;
};

export default createColumns;
